import { searchConfig } from '@cocoonspace/shared/config/search-config'
import { dateTimeUtils } from '@cocoonspace/shared/utils/date-time-utils'
import { Calendar, ChevronDown, X } from 'lucide-react'
import { useTranslation } from 'next-i18next'
import { useMemo, useState } from 'react'
import {
	DrawerPopover,
	DrawerPopoverClose,
	DrawerPopoverContent,
	DrawerPopoverTrigger,
} from '~/components/drawer-dialog'
import { Button } from '~/components/ui/button'
import { Card } from '~/components/ui/card'
import { Separator } from '~/components/ui/separator'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/components/ui/tabs'
import { cn } from '~/lib/utils'
import type { QueryFilters } from '../use-search-filters.hook'
import { MultiDate, SingleDate } from './date-pickers'

export const getDayValueAsArray = (value: string[] | string | undefined) => {
	if (!value) return undefined

	return Array.isArray(value) ? value : [value]
}

export type Timeslot = Pick<QueryFilters, 'day' | 'start' | 'end'>

export interface TimeslotFilterProps {
	value: Timeslot
	onChange: (timeslot: Timeslot) => void
	className?: string
}

export type TimeslotTab = 'hour' | 'day'

const getNextTimeSlot = () => {
	const currentTime = dateTimeUtils.utils.formatToNumberTime(dateTimeUtils())
	const defaultDay = dateTimeUtils().customFormat('dateTech')
	const defaultStart = searchConfig.timeSlots.find(
		(time, i, timeSlots) =>
			dateTimeUtils.utils.convertToNumberTime(time) >= currentTime &&
			i < timeSlots.length - 1,
	)

	const defaultEnd = !!defaultStart
		? searchConfig.timeSlots.find(
				(time) =>
					dateTimeUtils.utils.convertToNumberTime(time) -
						dateTimeUtils.utils.convertToNumberTime(defaultStart) ===
					100,
			)
		: undefined

	return {
		day: [defaultDay],
		start: defaultStart,
		end: defaultEnd,
	} as Timeslot
}

const TimeslotFilterContent = ({ value, onChange }: TimeslotFilterProps) => {
	const { t } = useTranslation()

	const [selectedTab, setSelectedTab] = useState<TimeslotTab>(
		!!value?.day && !value?.start && !value?.end ? 'day' : 'hour',
	)

	const [timeSlot, setTimeSlot] = useState<Timeslot>(() =>
		!value?.day && !value?.start && !value?.end ? getNextTimeSlot() : value,
	)

	const reset = () => {
		const value = {
			day: null,
			start: null,
			end: null,
		}

		onChange(value)
		setTimeSlot(value)
	}

	const isComplete =
		selectedTab === 'day'
			? !!timeSlot.day?.length
			: !!timeSlot.day &&
				!!timeSlot.start &&
				!!timeSlot.end &&
				dateTimeUtils.utils.convertToNumberTime(timeSlot.start) <
					dateTimeUtils.utils.convertToNumberTime(timeSlot.end)

	const handleTabChange = (tab: string) => {
		reset()
		setSelectedTab(tab as TimeslotTab)
	}

	return (
		<>
			<Tabs
				value={selectedTab}
				onValueChange={(tab) => {
					reset()
					handleTabChange(tab)
				}}
			>
				<TabsList
					variant='material'
					className='px-4'
				>
					<TabsTrigger
						variant='material'
						value='hour'
					>
						{t('search:dateFilter.byTheHour')}
					</TabsTrigger>

					<TabsTrigger
						variant='material'
						value='day'
					>
						{t('search:dateFilter.byTheDay')}
					</TabsTrigger>
				</TabsList>

				<Separator />

				<TabsContent
					value='hour'
					className='px-4'
				>
					<SingleDate
						isComplete
						className='pt-4'
						value={timeSlot}
						onChange={setTimeSlot}
					/>
				</TabsContent>

				<TabsContent
					value='day'
					className='px-4'
				>
					<MultiDate
						isComplete
						className='pt-4'
						value={timeSlot}
						onChange={setTimeSlot}
					/>
				</TabsContent>
			</Tabs>

			<Separator />

			<div className='flex items-center gap-x-2 p-4 pt-0'>
				{selectedTab === 'day' && (
					<p className='text-sm'>
						{t('search:dateFilter.daysSelected', {
							count: timeSlot.day?.length ?? 0,
						})}
					</p>
				)}

				<div className='ml-auto flex gap-x-4'>
					<DrawerPopoverClose asChild>
						<Button
							variant='link'
							size='sm'
							data-testid='cancel-btn'
						>
							{t('search:dateFilter.cancel')}
						</Button>
					</DrawerPopoverClose>

					<DrawerPopoverClose asChild>
						<Button
							disabled={!isComplete}
							variant='outline'
							size='sm'
							data-testid='validate-btn'
							onClick={() => {
								onChange(timeSlot)
							}}
						>
							{t('search:dateFilter.validate')}
						</Button>
					</DrawerPopoverClose>
				</div>
			</div>
		</>
	)
}

export const TimeslotFilter = ({
	value,
	onChange,
	className,
}: TimeslotFilterProps) => {
	const { t } = useTranslation()
	const [isOpen, setIsOpen] = useState(false)

	const humanReadableDate = useMemo(() => {
		if (!value?.day || !value?.day?.length) return ''

		const days = getDayValueAsArray(value?.day)?.map((day) =>
			dateTimeUtils(day).format('D MMM'),
		)

		if (value?.start && value?.end) {
			const start = value?.start?.replace(':', 'h')
			const end = value?.end?.replace(':', 'h')

			return `${days?.[0]}, ${start}-${end}`
		} else {
			return `${days?.join(', ')}`
		}
	}, [value])

	const reset = () => {
		onChange({
			day: null,
			start: null,
			end: null,
		})
	}

	return (
		<DrawerPopover
			open={isOpen}
			onOpenChange={setIsOpen}
		>
			<Card
				className={cn(
					'flex h-[32px] flex-row items-center overflow-hidden rounded text-sm',
					className,
				)}
			>
				<DrawerPopoverTrigger
					className='flex flex-1 flex-row items-center gap-2 overflow-hidden p-2'
					data-testid='date-filter'
				>
					<Calendar className='size-4 text-primary' />

					<div className='flex-1 overflow-hidden truncate text-left'>
						{humanReadableDate || t('search:dateFilter.date')}
					</div>

					{!humanReadableDate && (
						<ChevronDown
							strokeWidth={1}
							className='size-4'
						/>
					)}
				</DrawerPopoverTrigger>

				{!!humanReadableDate && (
					<button
						type='button'
						className='pr-2'
						onClick={(e) => {
							e.preventDefault()
							e.stopPropagation()
							reset()
							setIsOpen(false)
						}}
					>
						<X className='size-4 text-primary' />
					</button>
				)}
			</Card>

			<DrawerPopoverContent
				sideOffset={10}
				className='flex w-full flex-col gap-y-4 p-0 md:w-[550px]'
			>
				<TimeslotFilterContent
					value={value}
					onChange={onChange}
				/>
			</DrawerPopoverContent>
		</DrawerPopover>
	)
}
